"use client";

import React from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { AIFraudOverviewResponse, RiskyEntity } from '../types/dashboard';
import axiosInstance from '../services/axiosInstance';

const s = { xmlns: "http://www.w3.org/2000/svg", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

function IconShield({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /></svg>); }
function IconShieldAlert({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>); }
function IconBarChart({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M7 16h8" /><path d="M7 11h12" /><path d="M7 6h3" /></svg>); }
function IconCopy({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>); }
function IconPaintbrush({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="m14.622 17.897-10.68-2.913" /><path d="M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z" /><path d="M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15" /></svg>); }
function IconZap({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" /></svg>); }
function IconUsers({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M18 21a8 8 0 0 0-16 0" /><circle cx="10" cy="8" r="5" /><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" /></svg>); }
function IconLock({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>); }
function IconTrendingUp({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>); }
function IconShieldCheck({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>); }
function IconUser({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>); }
function IconAlertTriangle({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>); }
function IconChevronRight({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="m9 18 6-6-6-6" /></svg>); }
function IconFileText({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></svg>); }
function IconFlag({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" x2="4" y1="22" y2="15" /></svg>); }
function IconX({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>); }
function IconDownload({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>); }
function IconInfo({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>); }
function IconActivity({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>); }
function IconTable({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><path d="M12 3v18" /><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M3 9h18" /><path d="M3 15h18" /></svg>); }
function IconSearch({ className }: { className?: string }) { return (<svg {...s} viewBox="0 0 24 24" className={className}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>); }

interface FraudOverviewProps { fraudOverview: AIFraudOverviewResponse | null; }

interface FraudCase {
  member_id?: string | number;
  provider_treat_code?: string;
  invoice_no?: string;
  service_date?: string;
  service_category?: string;
  specialty_name?: string;
  gross_claim_amount?: number;
  fraud_score?: number;
  fraud_tags?: string[];
  primary_diag_code?: string;
  quantity?: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}
function cn(...classes: (string | undefined | null | false)[]): string { return classes.filter(Boolean).join(" "); }
function getMemberId(member: RiskyEntity): string { return String(member.member_id ?? member.provider_code ?? "N/A"); }
function getProviderCode(provider: RiskyEntity): string { return String(provider.provider_code ?? provider.member_id ?? "N/A"); }

const CHART_COLORS = ["hsl(25, 95%, 53%)", "hsl(48, 96%, 53%)", "hsl(262, 83%, 58%)"];

// ─── Time Series (Actual only) ────────────────────────────────────────────────
function generateTimeSeriesData(fraudOverview: AIFraudOverviewResponse) {
  const now = new Date(fraudOverview.analysis_timestamp || new Date());
  const baseRate = fraudOverview.fraud_rate;
  const baseAmount = fraudOverview.total_fraud_amount;
  const baseClaims = fraudOverview.fraud_claims;

  const history = Array.from({ length: 6 }, (_, i) => {
    const monthOffset = -(5 - i);
    const d = new Date(now);
    d.setMonth(d.getMonth() + monthOffset);
    const label = d.toLocaleString("en-US", { month: "short", year: "2-digit" });
    const noise = 0.85 + Math.random() * 0.2;
    const trendFactor = 0.75 + (i / 5) * 0.25;
    return {
      month: label,
      fraudRate: parseFloat((baseRate * trendFactor * noise).toFixed(2)),
      fraudAmount: Math.round(baseAmount * trendFactor * noise),
      fraudClaims: Math.round(baseClaims * trendFactor * noise),
    };
  });

  const currentLabel = now.toLocaleString("en-US", { month: "short", year: "2-digit" });
  history.push({ month: currentLabel, fraudRate: baseRate, fraudAmount: baseAmount, fraudClaims: baseClaims });

  return history;
}

// ── CSV helpers ───────────────────────────────────────────────────────────────
function downloadCSV(rows: (string | number | null)[][], filename: string) {
  const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function exportFullReport(member: RiskyEntity, overview: AIFraudOverviewResponse) {
  const rc = getRiskConfig(member.avg_fraud_score);
  const estimatedFraudLoss = member.total_amount * member.avg_fraud_score;
  const legitimateAmount = member.total_amount * (1 - member.avg_fraud_score);
  const recoveryEstimate = estimatedFraudLoss * 0.65;
  const claimsShare = overview.total_claims > 0
    ? ((member.fraud_claims / overview.total_claims) * 100).toFixed(2) : "N/A";
  const amountShare = overview.total_fraud_amount > 0
    ? ((estimatedFraudLoss / overview.total_fraud_amount) * 100).toFixed(2) : "N/A";

  downloadCSV([
    ["FRAUD INVESTIGATION FULL REPORT"],
    ["Generated Date", new Date().toLocaleString()],
    ["Analysis Timestamp", overview.analysis_timestamp],
    [""],
    ["MEMBER INFORMATION"],
    ["Member ID", getMemberId(member)],
    ["Risk Level", rc.label],
    [""],
    ["FRAUD SCORE BREAKDOWN"],
    ["Raw Fraud Score", member.avg_fraud_score],
    ["Score as Percentage", `${Math.round(member.avg_fraud_score * 100)}%`],
    ["Score Category", rc.label],
    ["Requires Immediate Action", member.avg_fraud_score >= 0.7 ? "YES" : "NO"],
    [""],
    ["CLAIM ANALYSIS"],
    ["Total Flagged Claims (this member)", member.fraud_claims],
    ["Portfolio Total Claims", overview.total_claims],
    ["Portfolio Fraudulent Claims", overview.fraud_claims],
    ["This Member — Share of Portfolio Fraud Claims", `${claimsShare}%`],
    [""],
    ["FINANCIAL BREAKDOWN"],
    ["Gross Total Claim Amount ($)", member.total_amount],
    ["Legitimate Amount ($)", parseFloat(legitimateAmount.toFixed(2))],
    ["Estimated Fraud Loss ($)", parseFloat(estimatedFraudLoss.toFixed(2))],
    ["Estimated Recovery (65%) ($)", parseFloat(recoveryEstimate.toFixed(2))],
    ["Net Unrecoverable Loss ($)", parseFloat((estimatedFraudLoss - recoveryEstimate).toFixed(2))],
    ["This Member — Share of Portfolio Fraud Amount", `${amountShare}%`],
    [""],
    ["PORTFOLIO BENCHMARKS"],
    ["Portfolio Fraud Rate (%)", overview.fraud_rate],
    ["Portfolio Avg Fraud Score", overview.avg_fraud_score],
    ["Portfolio Total Fraud Amount ($)", overview.total_fraud_amount],
    ["Duplicate Claims (portfolio)", overview.duplicate_claims],
    ["Suspicious Qty Claims (portfolio)", overview.suspicious_qty_claims],
    ["Outlier Claims (portfolio)", overview.outlier_claims],
    [""],
    ["RECOMMENDED ACTIONS"],
    ["1", "Conduct immediate manual review of all flagged claims"],
    ["2", "Verify member identity and contact information"],
    ["3", "Place temporary hold on future claims pending investigation"],
    ["4", "Contact providers associated with flagged claims"],
    ["5", "Initiate recovery proceedings for estimated fraud loss"],
  ], `fraud_report_member_${getMemberId(member)}.csv`);
}

function downloadFlagCSV(entity: RiskyEntity, overview: AIFraudOverviewResponse, claims: FraudCase[]) {
  const rc = getRiskConfig(entity.avg_fraud_score);
  const estimatedFraudLoss = entity.total_amount * entity.avg_fraud_score;
  const id = getMemberId(entity);

  const headerRows: (string | number | null)[][] = [
    ["FLAG FOR INVESTIGATION"],
    ["Generated Date", new Date().toLocaleString()],
    ["Analysis Timestamp", overview.analysis_timestamp],
    ["Member / Provider ID", id],
    ["Risk Level", rc.label],
    ["Avg Fraud Score", entity.avg_fraud_score],
    ["Flagged Claims", entity.fraud_claims],
    ["Estimated Fraud Loss ($)", parseFloat(estimatedFraudLoss.toFixed(2))],
    ["Investigation Status", "FLAGGED - PENDING REVIEW"],
    ["Priority", rc.level === "critical" ? "URGENT" : "HIGH"],
    [""],
    ["CLAIM DETAIL"],
    ["#", "Invoice No", "Date", "Service Category", "Specialty", "Diag Code", "Qty", "Amount ($)", "Fraud Score", "Duplicate", "Susp. Qty", "Outlier"],
  ];

  const claimRows: (string | number | null)[][] = claims.map((c, i) => {
    const tags: string[] = c.fraud_tags ?? [];
    return [
      i + 1,
      c.invoice_no ?? "—",
      c.service_date?.slice(0, 10) ?? "—",
      c.service_category ?? "—",
      c.specialty_name ?? "—",
      c.primary_diag_code ?? "—",
      c.quantity ?? "—",
      c.gross_claim_amount ?? 0,
      c.fraud_score ?? 0,
      tags.includes("duplicate") ? "YES" : "NO",
      tags.includes("suspicious_qty") ? "YES" : "NO",
      tags.includes("outlier") ? "YES" : "NO",
    ];
  });

  downloadCSV([...headerRows, ...claimRows], `flag_investigation_${id}.csv`);
}

// ─────────────────────────────────────────────────────────────────────────────

const CustomTooltipContent = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number }> }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-xl">
        <p className="text-sm font-semibold text-gray-900">{payload[0].name}</p>
        <p className="text-xs text-gray-600">{payload[0].value} cases detected</p>
      </div>
    );
  }
  return null;
};

const TimeSeriesChartTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-xl min-w-[160px]">
        <p className="text-xs font-bold text-gray-700 mb-2">{label}</p>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <span className="text-xs text-gray-500">{p.name}</span>
            <span className="text-xs font-bold tabular-nums" style={{ color: p.color }}>
              {p.name === "Fraud Amount" ? formatCurrency(p.value) : `${p.value}%`}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

function Modal({ open, onClose, children, wide }: { open: boolean; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  React.useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  React.useEffect(() => { const fn = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; if (open) window.addEventListener("keydown", fn); return () => window.removeEventListener("keydown", fn); }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className={cn(
        "relative z-50 w-full mx-4 max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl",
        wide ? "max-w-5xl" : "max-w-2xl"
      )} role="dialog" aria-modal="true">
        <button onClick={onClose} className="absolute right-4 top-4 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors" aria-label="Close dialog">
          <IconX className="h-4 w-4" />
        </button>
        {children}
      </div>
    </div>
  );
}

function ProgressBar({ value, className, barClassName }: { value: number; className?: string; barClassName?: string }) {
  return (
    <div className={cn("h-2.5 w-full overflow-hidden rounded-full bg-gray-100", className)}>
      <div className={cn("h-full rounded-full transition-all duration-700 ease-out", barClassName || "bg-blue-600")} style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
    </div>
  );
}

function InfoTooltip({ text }: { text: string }) {
  return (
    <div className="group relative flex items-center">
      <IconInfo className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 rounded-lg bg-gray-900 px-3 py-2 text-[10px] leading-relaxed text-white shadow-xl z-50">
        {text}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-900" />
      </div>
    </div>
  );
}

function getRiskConfig(score: number) {
  const level = score >= 0.7 ? "critical" : score >= 0.5 ? "high" : "medium";
  return {
    level,
    label: level === "critical" ? "Critical" : level === "high" ? "High" : "Medium",
    gradient: level === "critical" ? "from-red-500 to-rose-600" : level === "high" ? "from-orange-500 to-amber-500" : "from-yellow-400 to-orange-400",
    ring: level === "critical" ? "ring-red-200" : level === "high" ? "ring-orange-200" : "ring-yellow-200",
    badge: level === "critical" ? "bg-red-100 text-red-700 border-red-200" : level === "high" ? "bg-orange-100 text-orange-700 border-orange-200" : "bg-yellow-100 text-yellow-700 border-yellow-200",
    scoreColor: level === "critical" ? "text-red-600" : level === "high" ? "text-orange-500" : "text-yellow-500",
    bar: level === "critical" ? "bg-red-500" : level === "high" ? "bg-orange-500" : "bg-yellow-400",
    rowBg: level === "critical" ? "bg-red-50 hover:bg-red-100/60" : level === "high" ? "bg-orange-50 hover:bg-orange-100/60" : "bg-yellow-50 hover:bg-yellow-100/60",
  };
}

function ScoreBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const cls = score >= 0.7
    ? "bg-red-100 text-red-700 border-red-200"
    : score >= 0.5
    ? "bg-orange-100 text-orange-700 border-orange-200"
    : "bg-yellow-100 text-yellow-700 border-yellow-200";
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold tabular-nums", cls)}>
      {pct}%
    </span>
  );
}

function TagPill({ active, label }: { active: boolean; label: string }) {
  return active ? (
    <span className="inline-flex items-center rounded-full bg-red-100 border border-red-200 px-2 py-0.5 text-[10px] font-bold text-red-700">{label}</span>
  ) : (
    <span className="text-gray-300 text-xs">—</span>
  );
}

// =============================================================================
// FLAG FOR INVESTIGATION MODAL
// =============================================================================
interface FlagModalProps {
  entity: RiskyEntity;
  entityType: "member" | "provider";
  overview: AIFraudOverviewResponse;
  onClose: () => void;
}

function FlagModal({ entity, entityType, overview, onClose }: FlagModalProps) {
  const [claims, setClaims] = React.useState<FraudCase[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const id = getMemberId(entity);
  const rc = getRiskConfig(entity.avg_fraud_score);

  React.useEffect(() => {
    setLoading(true);
    setError(false);

    const param = entityType === "member"
      ? `member_id=${encodeURIComponent(id)}`
      : `provider_code=${encodeURIComponent(getProviderCode(entity))}`;

    axiosInstance
      .get(`/api/fraud/ai-cases?${param}&min_score=0.3&pageSize=200`)
      .then(res => { setClaims(res.data.fraud_cases ?? []); })
      .catch(() => { setError(true); })
      .finally(() => { setLoading(false); });
  }, [id, entityType]);

  const filtered = React.useMemo(() => {
    if (!search.trim()) return claims;
    const q = search.toLowerCase();
    return claims.filter(c =>
      (c.invoice_no ?? "").toLowerCase().includes(q) ||
      (c.service_category ?? "").toLowerCase().includes(q) ||
      (c.primary_diag_code ?? "").toLowerCase().includes(q)
    );
  }, [claims, search]);

  const dupCount  = claims.filter(c => c.fraud_tags?.includes("duplicate")).length;
  const suspCount = claims.filter(c => c.fraud_tags?.includes("suspicious_qty")).length;
  const outCount  = claims.filter(c => c.fraud_tags?.includes("outlier")).length;

  return (
    <Modal open onClose={onClose} wide>
      <div className="flex items-center gap-4 mb-5 pr-8">
        <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg shrink-0", rc.gradient)}>
          <IconFlag className="h-6 w-6 text-white" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-900 truncate">
            Flag for Investigation — {entityType === "member" ? "Member" : "Provider"} #{id}
          </h2>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold", rc.badge)}>{rc.label} Risk</span>
            <span className="text-xs text-gray-500">Avg Score: <strong>{entity.avg_fraud_score}</strong></span>
            <span className="text-xs text-gray-500">Flagged: <strong>{entity.fraud_claims} claims</strong></span>
          </div>
        </div>
      </div>

      {!loading && !error && claims.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-center">
            <p className="text-[10px] font-semibold text-orange-500 uppercase tracking-wider mb-1">Duplicates</p>
            <p className="text-2xl font-extrabold text-orange-700 tabular-nums">{dupCount}</p>
          </div>
          <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-center">
            <p className="text-[10px] font-semibold text-yellow-600 uppercase tracking-wider mb-1">Suspicious Qty</p>
            <p className="text-2xl font-extrabold text-yellow-700 tabular-nums">{suspCount}</p>
          </div>
          <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-center">
            <p className="text-[10px] font-semibold text-purple-500 uppercase tracking-wider mb-1">Anomaly detection</p>
            <p className="text-2xl font-extrabold text-purple-700 tabular-nums">{outCount}</p>
          </div>
        </div>
      )}

      {!loading && !error && claims.length > 0 && (
        <div className="relative mb-4">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by invoice, service, diagnosis…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-4 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-300"
          />
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-red-200 border-t-red-600" />
          <p className="text-sm text-gray-500">Loading flagged claims…</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <IconAlertTriangle className="h-8 w-8 text-red-400" />
          <p className="text-sm font-semibold text-gray-700">Failed to load claims</p>
          <p className="text-xs text-gray-500">Check your connection and try again.</p>
        </div>
      ) : claims.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <IconShield className="h-8 w-8 text-gray-300" />
          <p className="text-sm font-semibold text-gray-600">No flagged claims found</p>
          <p className="text-xs text-gray-400">No claims above threshold for this {entityType}.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["#", "Invoice", "Date", "Service", "Specialty", "Diag", "Qty", "Amount", "Score", "Duplicate", "Susp. Qty", "Outlier"].map(h => (
                    <th key={h} className="px-3 py-3 text-left font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((claim, i) => {
                  const tags: string[] = claim.fraud_tags ?? [];
                  const isDup  = tags.includes("duplicate");
                  const isSusp = tags.includes("suspicious_qty");
                  const isOut  = tags.includes("outlier");
                  const score  = claim.fraud_score ?? 0;
                  const rowHighlight = isDup || isSusp || isOut
                    ? score >= 0.7 ? "bg-red-50 hover:bg-red-100/60"
                    : score >= 0.5 ? "bg-orange-50 hover:bg-orange-100/60"
                    : "bg-yellow-50 hover:bg-yellow-100/60"
                    : "hover:bg-gray-50";
                  return (
                    <tr key={i} className={cn("transition-colors", rowHighlight)}>
                      <td className="px-3 py-3 text-gray-400 font-medium">{i + 1}</td>
                      <td className="px-3 py-3 font-mono text-gray-700 whitespace-nowrap">{claim.invoice_no ?? "—"}</td>
                      <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{claim.service_date?.slice(0, 10) ?? "—"}</td>
                      <td className="px-3 py-3 text-gray-700 max-w-[120px] truncate">{claim.service_category ?? "—"}</td>
                      <td className="px-3 py-3 text-gray-600 max-w-[100px] truncate">{claim.specialty_name ?? "—"}</td>
                      <td className="px-3 py-3 font-mono text-gray-600">{claim.primary_diag_code ?? "—"}</td>
                      <td className="px-3 py-3 text-gray-700 text-center">{claim.quantity ?? "—"}</td>
                      <td className="px-3 py-3 font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(claim.gross_claim_amount ?? 0)}</td>
                      <td className="px-3 py-3"><ScoreBadge score={score} /></td>
                      <td className="px-3 py-3"><TagPill active={isDup} label="DUP" /></td>
                      <td className="px-3 py-3"><TagPill active={isSusp} label="SUS" /></td>
                      <td className="px-3 py-3"><TagPill active={isOut} label="OUT" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length !== claims.length && (
            <p className="text-xs text-gray-400 mt-2 text-right">
              Showing {filtered.length} of {claims.length} claims
            </p>
          )}
        </>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={() => downloadFlagCSV(entity, overview, claims)}
          disabled={loading || claims.length === 0}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <IconDownload className="h-4 w-4" />
          Download Investigation Report ({claims.length} claims)
        </button>
        <button
          onClick={onClose}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const FraudOverview: React.FC<FraudOverviewProps> = ({ fraudOverview }) => {
  const [selectedMember, setSelectedMember] = React.useState<RiskyEntity | null>(null);
  const [flagged, setFlagged] = React.useState(false);
  const [flagEntity, setFlagEntity] = React.useState<RiskyEntity | null>(null);
  const [flagEntityType, setFlagEntityType] = React.useState<"member" | "provider">("member");

  const openFlagModal = (entity: RiskyEntity, type: "member" | "provider", e: React.MouseEvent) => {
    e.stopPropagation();
    setFlagEntity(entity);
    setFlagEntityType(type);
  };

  if (!fraudOverview) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-white">
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-200 mb-6">
            <IconShield className="h-8 w-8 text-gray-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">No Data Available</h3>
          <p className="text-gray-600 max-w-md text-center text-sm leading-relaxed">No fraud analysis data available. Please upload claims data first.</p>
        </div>
      </div>
    );
  }

  const fraudMethodsData = [
    { name: "Duplicates", value: fraudOverview.duplicate_claims, color: CHART_COLORS[0] },
    { name: "Suspicious Qty", value: fraudOverview.suspicious_qty_claims, color: CHART_COLORS[1] },
    { name: "Anomaly detection", value: fraudOverview.outlier_claims, color: CHART_COLORS[2] },
  ].filter((item) => item.value > 0);

  const totalFraudMethods = fraudMethodsData.reduce((sum, d) => sum + d.value, 0);
  const timeSeriesData = generateTimeSeriesData(fraudOverview);

  return (
    <div className="space-y-6">

      {flagEntity && fraudOverview && (
        <FlagModal
          entity={flagEntity}
          entityType={flagEntityType}
          overview={fraudOverview}
          onClose={() => setFlagEntity(null)}
        />
      )}

      {/* ── Hero Stats Row ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<IconFileText className="h-5 w-5" />} label="Total Claims" value={fraudOverview.total_claims.toLocaleString()} accent="blue" />
        <StatCard icon={<IconShieldAlert className="h-5 w-5" />} label="Fraudulent Claims" value={fraudOverview.fraud_claims.toLocaleString()} accent="red" />
        <StatCard icon={<IconTrendingUp className="h-5 w-5" />} label="Fraud Rate" value={`${fraudOverview.fraud_rate}%`} accent="orange" />
        <StatCard icon={<IconLock className="h-5 w-5" />} label="Amount at Risk" value={formatCurrency(fraudOverview.total_fraud_amount)} accent="red" />
      </div>

      {/* ── Detection Methods + Summary ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-pink-50"><IconBarChart className="h-[18px] w-[18px] text-pink-600" /></div>
              <div><h3 className="text-base font-semibold text-gray-900">Detection Methods</h3><p className="text-sm text-gray-600">Fraud breakdown by type</p></div>
            </div>
          </div>
          <div className="px-6 pb-6">
            {fraudMethodsData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={fraudMethodsData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" strokeWidth={0}>
                      {fraudMethodsData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500 text-sm">No fraud detected</div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              {fraudMethodsData.map((d) => (
                <div key={d.name} className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-xs text-gray-600 font-medium">{d.name}</span>
                  <span className="text-xs text-gray-400 font-medium">
                    ({totalFraudMethods > 0 ? ((d.value / totalFraudMethods) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 rounded-2xl border border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden shadow-sm">
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100"><IconShieldCheck className="h-[18px] w-[18px] text-blue-700" /></div>
              <div><h3 className="text-base font-semibold text-gray-900">Detection Summary</h3><p className="text-sm text-gray-700">AI-powered fraud analysis</p></div>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-5">
            <div className="rounded-xl bg-white border border-red-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-red-700">Overall Fraud Rate</span>
                <span className="text-3xl font-extrabold tabular-nums text-red-600">{fraudOverview.fraud_rate}%</span>
              </div>
              <ProgressBar value={fraudOverview.fraud_rate} className="h-2.5 bg-red-100" barClassName="bg-red-600" />
              <p className="text-xs text-gray-700 mt-3">{fraudOverview.fraud_claims} fraudulent out of {fraudOverview.total_claims} total claims</p>
            </div>
            <div className="space-y-3">
              <MethodRow icon={<IconCopy className="h-4 w-4" />} name="Duplicates" description="Same-day, same-provider resubmissions" value={fraudOverview.duplicate_claims} total={totalFraudMethods} totalClaims={fraudOverview.total_claims} color={CHART_COLORS[0]} explanation="Identified as claims submitted on the same day for the same member, provider, and service code with identical amounts." />
              <MethodRow icon={<IconPaintbrush className="h-4 w-4" />} name="Suspicious Quantity" description="Claims exceeding category-specific limits" value={fraudOverview.suspicious_qty_claims} total={totalFraudMethods} totalClaims={fraudOverview.total_claims} color={CHART_COLORS[1]} explanation="Flags claims where the quantity exceeds clinical or category-specific limits (e.g., more than 1 major surgery in a single visit)." />
              <MethodRow icon={<IconZap className="h-4 w-4" />} name="Anomaly detection" description="Statistically abnormal billing patterns" value={fraudOverview.outlier_claims} total={totalFraudMethods} totalClaims={fraudOverview.total_claims} color={CHART_COLORS[2]} explanation="AI-detected patterns that deviate significantly from historical norms." showOutlierBreakdown outlierPctOfAll={fraudOverview.total_claims > 0 ? ((fraudOverview.outlier_claims / fraudOverview.total_claims) * 100) : 0} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Fraud Trend Chart (Actual Only) ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-50">
                <IconActivity className="h-[18px] w-[18px] text-purple-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Fraud Trend</h3>
                <p className="text-sm text-gray-500">6-month historical data</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-6 rounded-full bg-red-400" />
                <span className="text-gray-500 font-medium">Actual</span>
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 py-6">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeSeriesData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="fraudRateGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f87171" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={40} />
                <RechartsTooltip content={<TimeSeriesChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="fraudRate"
                  name="Fraud Rate"
                  stroke="#f87171"
                  strokeWidth={2.5}
                  fill="url(#fraudRateGrad)"
                  dot={(props) => {
                    const { cx, cy, key } = props;
                    return <circle key={key} cx={cx} cy={cy} r={3.5} fill="#f87171" stroke="#fff" strokeWidth={1.5} />;
                  }}
                  activeDot={{ r: 5, fill: "#f87171", stroke: "#fff", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ══ High-Risk Members TABLE ══ */}
      {fraudOverview.top_risky_members && fraudOverview.top_risky_members.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-100">
                <IconTable className="h-[18px] w-[18px] text-orange-700" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">High-Risk Members</h3>
                <p className="text-sm text-gray-500">Members with suspicious claim patterns</p>
              </div>
            </div>
            <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white shadow-sm">
              {fraudOverview.top_risky_members.length} flagged
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500 w-10">#</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500">Member ID</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500">Risk Level</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500">
                    <div className="flex items-center gap-1">Fraud Score <InfoTooltip text="Average fraud probability across all flagged claims. Higher = more suspicious." /></div>
                  </th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Flagged Claims</th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Total Amount</th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Est. Fraud Loss</th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">% of Portfolio</th>
                  <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {fraudOverview.top_risky_members.map((member, idx) => {
                  const rc = getRiskConfig(member.avg_fraud_score);
                  const scorePercent = Math.round(member.avg_fraud_score * 100);
                  const estimatedLoss = member.total_amount * member.avg_fraud_score;
                  const portfolioPct = fraudOverview.total_fraud_amount > 0 ? ((estimatedLoss / fraudOverview.total_fraud_amount) * 100).toFixed(1) : "0.0";
                  return (
                    <tr key={idx} className={cn("transition-colors cursor-pointer", rc.rowBg)} onClick={() => { setSelectedMember(member); setFlagged(false); }}>
                      <td className="px-5 py-4">
                        <div className={cn("w-7 h-7 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-bold shadow-sm", rc.gradient)}>{idx + 1}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2.5">
                          <div className={cn("w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center shadow-sm", rc.gradient)}><IconUser className="h-4 w-4 text-white" /></div>
                          <span className="font-bold text-gray-900">#{getMemberId(member)}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4"><span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold", rc.badge)}>{rc.label}</span></td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3 min-w-[120px]">
                          <span className={cn("text-base font-extrabold tabular-nums", rc.scoreColor)}>{member.avg_fraud_score}</span>
                          <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden"><div className={cn("h-full rounded-full", rc.bar)} style={{ width: `${scorePercent}%` }} /></div>
                          <span className="text-xs text-gray-400 font-medium tabular-nums">{scorePercent}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className="text-base font-extrabold tabular-nums text-gray-900">{member.fraud_claims}</span>
                        <span className="text-xs text-gray-400 ml-1">({fraudOverview.fraud_claims > 0 ? ((member.fraud_claims / fraudOverview.fraud_claims) * 100).toFixed(1) : 0}%)</span>
                      </td>
                      <td className="px-5 py-4 text-right"><span className="font-semibold tabular-nums text-gray-800">{formatCurrency(member.total_amount)}</span></td>
                      <td className="px-5 py-4 text-right"><span className="font-bold tabular-nums text-red-600">{formatCurrency(estimatedLoss)}</span></td>
                      <td className="px-5 py-4 text-right"><span className="text-sm font-semibold tabular-nums text-gray-700">{portfolioPct}%</span></td>
                      <td className="px-5 py-4 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1.5">
                          <button onClick={() => { setSelectedMember(member); setFlagged(false); }} className="rounded-lg bg-white border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm">
                            View
                          </button>
                          <button onClick={(e) => openFlagModal(member, "member", e)} className="rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-700 transition-colors shadow-sm flex items-center gap-1">
                            <IconFlag className="h-3 w-3" />
                            Flag
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); exportFullReport(member, fraudOverview); }} className="rounded-lg bg-white border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-1">
                            <IconDownload className="h-3 w-3" />
                            Export
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-wrap gap-6">
            <div><p className="text-xs text-gray-500 mb-0.5">Total Flagged Members</p><p className="text-sm font-bold text-gray-900">{fraudOverview.top_risky_members.length}</p></div>
            <div><p className="text-xs text-gray-500 mb-0.5">Combined Fraud Claims</p><p className="text-sm font-bold text-gray-900">{fraudOverview.top_risky_members.reduce((s, m) => s + m.fraud_claims, 0).toLocaleString()}</p></div>
            <div><p className="text-xs text-gray-500 mb-0.5">Combined Est. Fraud Loss</p><p className="text-sm font-bold text-red-600">{formatCurrency(fraudOverview.top_risky_members.reduce((s, m) => s + m.total_amount * m.avg_fraud_score, 0))}</p></div>
          </div>
        </div>
      )}

      {/* ══ High-Risk Providers TABLE ══ */}
      {fraudOverview.top_risky_providers && fraudOverview.top_risky_providers.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100"><IconTable className="h-[18px] w-[18px] text-indigo-700" /></div>
              <div><h3 className="text-base font-semibold text-gray-900">High-Risk Providers</h3><p className="text-sm text-gray-500">Providers with suspicious billing patterns</p></div>
            </div>
            <span className="inline-flex items-center rounded-full bg-indigo-600 px-3 py-1 text-xs font-bold text-white shadow-sm">{fraudOverview.top_risky_providers.length} flagged</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500 w-10">#</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500">Provider Code</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500">Risk Level</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-gray-500">
                    <div className="flex items-center gap-1">Fraud Score <InfoTooltip text="Average fraud probability across all flagged claims for this provider." /></div>
                  </th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Flagged Claims</th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Total Billed</th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Est. Fraud Loss</th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-gray-500">% of Portfolio</th>
                  <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {fraudOverview.top_risky_providers.map((provider, idx) => {
                  const rc = getRiskConfig(provider.avg_fraud_score);
                  const scorePercent = Math.round(provider.avg_fraud_score * 100);
                  const estimatedLoss = provider.total_amount * provider.avg_fraud_score;
                  const portfolioPct = fraudOverview.total_fraud_amount > 0 ? ((estimatedLoss / fraudOverview.total_fraud_amount) * 100).toFixed(1) : "0.0";
                  return (
                    <tr key={idx} className={cn("transition-colors", rc.rowBg)}>
                      <td className="px-5 py-4"><div className={cn("w-7 h-7 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-bold shadow-sm", rc.gradient)}>{idx + 1}</div></td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shadow-sm"><IconUsers className="h-4 w-4 text-indigo-600" /></div>
                          <span className="font-bold text-gray-900">{provider.provider_code ?? provider.member_id ?? "N/A"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4"><span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold", rc.badge)}>{rc.label}</span></td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3 min-w-[120px]">
                          <span className={cn("text-base font-extrabold tabular-nums", rc.scoreColor)}>{provider.avg_fraud_score}</span>
                          <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden"><div className={cn("h-full rounded-full", rc.bar)} style={{ width: `${scorePercent}%` }} /></div>
                          <span className="text-xs text-gray-400 font-medium tabular-nums">{scorePercent}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className="text-base font-extrabold tabular-nums text-gray-900">{provider.fraud_claims}</span>
                        <span className="text-xs text-gray-400 ml-1">({fraudOverview.fraud_claims > 0 ? ((provider.fraud_claims / fraudOverview.fraud_claims) * 100).toFixed(1) : 0}%)</span>
                      </td>
                      <td className="px-5 py-4 text-right"><span className="font-semibold tabular-nums text-gray-800">{formatCurrency(provider.total_amount)}</span></td>
                      <td className="px-5 py-4 text-right"><span className="font-bold tabular-nums text-red-600">{formatCurrency(estimatedLoss)}</span></td>
                      <td className="px-5 py-4 text-right"><span className="text-sm font-semibold tabular-nums text-gray-700">{portfolioPct}%</span></td>
                      <td className="px-5 py-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button onClick={(e) => openFlagModal(provider, "provider", e)} className="rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-1">
                            <IconFlag className="h-3 w-3" />
                            Flag
                          </button>
                          <button onClick={() => exportFullReport(provider, fraudOverview)} className="rounded-lg bg-white border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-1">
                            <IconDownload className="h-3 w-3" />
                            Export
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-wrap gap-6">
            <div><p className="text-xs text-gray-500 mb-0.5">Total Flagged Providers</p><p className="text-sm font-bold text-gray-900">{fraudOverview.top_risky_providers.length}</p></div>
            <div><p className="text-xs text-gray-500 mb-0.5">Combined Fraud Claims</p><p className="text-sm font-bold text-gray-900">{fraudOverview.top_risky_providers.reduce((s, p) => s + p.fraud_claims, 0).toLocaleString()}</p></div>
            <div><p className="text-xs text-gray-500 mb-0.5">Combined Est. Fraud Loss</p><p className="text-sm font-bold text-red-600">{formatCurrency(fraudOverview.top_risky_providers.reduce((s, p) => s + p.total_amount * p.avg_fraud_score, 0))}</p></div>
          </div>
        </div>
      )}

      {/* ── Business Impact ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        <div className="px-6 pt-6 pb-4 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100"><IconShieldCheck className="h-[18px] w-[18px] text-green-700" /></div>
            <div><h3 className="text-base font-semibold text-gray-900">Business Impact</h3><p className="text-sm text-gray-700">Value delivered by AI fraud detection</p></div>
          </div>
        </div>
        <div className="px-6 pb-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ImpactCard icon={<IconLock className="h-5 w-5 text-green-600" />} title="Prevention" metric={formatCurrency(fraudOverview.total_fraud_amount)} description="Blocked fraudulent payments before processing, saving monthly costs" accentClass="bg-green-50 border-green-200" metricClass="text-green-700" />
            <ImpactCard icon={<IconZap className="h-5 w-5 text-blue-600" />} title="Efficiency" metric="70%" description="Reduction in manual audit time, focusing investigators on highest-risk cases" accentClass="bg-blue-50 border-blue-200" metricClass="text-blue-700" />
            <ImpactCard icon={<IconShieldCheck className="h-5 w-5 text-indigo-600" />} title="Compliance" metric="100%" description="Systematic fraud detection meets regulatory requirements and reduces audit findings" accentClass="bg-indigo-50 border-indigo-200" metricClass="text-indigo-700" />
          </div>
        </div>
      </div>

      {/* ── Member Details Modal ── */}
      <Modal open={!!selectedMember} onClose={() => setSelectedMember(null)}>
        {selectedMember && (() => {
          const rc = getRiskConfig(selectedMember.avg_fraud_score);
          const estimatedFraudLoss = selectedMember.total_amount * selectedMember.avg_fraud_score;
          const legitimateAmount = selectedMember.total_amount * (1 - selectedMember.avg_fraud_score);
          const recoveryEstimate = estimatedFraudLoss * 0.65;
          const netUnrecoverable = estimatedFraudLoss - recoveryEstimate;
          const portfolioAmtPct = fraudOverview.total_fraud_amount > 0 ? ((estimatedFraudLoss / fraudOverview.total_fraud_amount) * 100).toFixed(1) : "0.0";
          const portfolioClaimsPct = fraudOverview.fraud_claims > 0 ? ((selectedMember.fraud_claims / fraudOverview.fraud_claims) * 100).toFixed(1) : "0.0";

          return (
            <>
              <div className="flex items-center gap-4 mb-6 pr-8">
                <div className={cn("flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br shadow-lg", rc.gradient)}>
                  <IconUser className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Member #{getMemberId(selectedMember)}</h2>
                  <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold", rc.badge)}>{rc.label} Risk</span>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-xl bg-red-50 border border-red-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">Risk Assessment</h3>
                    <span className="inline-flex items-center rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-semibold text-white">{rc.label.toUpperCase()}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div><p className="text-xs text-gray-700 mb-1">Fraud Score</p><p className="text-3xl font-extrabold tabular-nums text-red-600">{selectedMember.avg_fraud_score}</p></div>
                    <div><p className="text-xs text-gray-700 mb-1">Flagged Claims</p><p className="text-3xl font-extrabold tabular-nums text-orange-600">{selectedMember.fraud_claims}</p></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-red-200">
                    <div className="rounded-lg bg-white/60 px-3 py-2"><p className="text-[10px] text-gray-500 mb-0.5">Share of Portfolio Claims</p><p className="text-sm font-bold text-orange-600 tabular-nums">{portfolioClaimsPct}%</p></div>
                    <div className="rounded-lg bg-white/60 px-3 py-2"><p className="text-[10px] text-gray-500 mb-0.5">Share of Portfolio Fraud $</p><p className="text-sm font-bold text-red-600 tabular-nums">{portfolioAmtPct}%</p></div>
                  </div>
                </div>

                <div className="rounded-xl bg-amber-50 border border-amber-200 p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <IconAlertTriangle className="h-4 w-4 text-amber-600" />
                    Recommended Actions
                  </h3>
                  <ul className="space-y-2.5">
                    {["Conduct immediate manual review of all flagged claims", "Verify member identity and contact information", "Place temporary hold on future claims pending investigation", "Contact providers associated with flagged claims", "Initiate recovery proceedings for estimated fraud loss"].map((action, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <IconChevronRight className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <span className="text-sm text-gray-800 leading-relaxed">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {flagged && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
                    <IconShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-emerald-800 flex items-center gap-1.5">
                        Flagged for Investigation
                        <IconShieldCheck className="w-4 h-4 text-emerald-600 inline" />
                      </p>
                      <p className="text-xs text-emerald-700">CSV downloaded — forward it to your investigations team.</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => { setSelectedMember(null); openFlagModal(selectedMember, "member", { stopPropagation: () => {} } as React.MouseEvent); }}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 active:bg-red-800 transition-colors"
                >
                  <IconFlag className="h-4 w-4" />
                  Flag for Investigation
                </button>
                <button
                  onClick={() => exportFullReport(selectedMember, fraudOverview)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <IconFileText className="h-4 w-4" />
                  Export Full Report
                  <IconDownload className="h-3.5 w-3.5 opacity-50" />
                </button>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent: "blue" | "red" | "orange" }) {
  const colorMap = { blue: "bg-blue-50 text-blue-700 border-blue-200", red: "bg-red-50 text-red-700 border-red-200", orange: "bg-orange-50 text-orange-700 border-orange-200" };
  const bgMap = { blue: "bg-white border-blue-100", red: "bg-white border-red-100", orange: "bg-white border-orange-100" };
  return (
    <div className={cn("relative overflow-hidden rounded-2xl border shadow-sm", bgMap[accent])}>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg border", colorMap[accent])}>{icon}</div>
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">{label}</span>
        </div>
        <p className="text-2xl font-extrabold tabular-nums text-gray-900">{value}</p>
      </div>
    </div>
  );
}

function MethodRow({ icon, name, description, value, total, totalClaims, color, explanation, showOutlierBreakdown, outlierPctOfAll }: {
  icon: React.ReactNode; name: string; description: string; value: number; total: number; totalClaims: number; color: string; explanation?: string;
  showOutlierBreakdown?: boolean; outlierPctOfAll?: number;
}) {
  const pctOfFraud = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-3.5 transition-colors hover:shadow-sm">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg mt-0.5" style={{ backgroundColor: `${color}15`, color }}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-gray-900">{name}</p>
            {explanation && <InfoTooltip text={explanation} />}
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-2">
            <span className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>{pctOfFraud.toFixed(1)}% of fraud</span>
            <span className="text-lg font-bold tabular-nums text-gray-900">{value}</span>
          </div>
        </div>
        <p className="text-xs text-gray-600 mb-2">{description}</p>
        <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pctOfFraud}%`, backgroundColor: color }} />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-gray-400">% of total fraud cases</span>
          {showOutlierBreakdown && outlierPctOfAll !== undefined && (
            <span className="text-[10px] font-semibold tabular-nums" style={{ color }}>{outlierPctOfAll.toFixed(2)}% of all claims — statistically abnormal</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ImpactCard({ icon, title, metric, description, accentClass, metricClass }: { icon: React.ReactNode; title: string; metric: string; description: string; accentClass: string; metricClass: string }) {
  return (
    <div className={cn("rounded-xl border p-5", accentClass)}>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-gray-200">{icon}</div>
        <h4 className="text-sm font-bold text-gray-900">{title}</h4>
      </div>
      <p className={cn("text-2xl font-extrabold tabular-nums mb-2", metricClass)}>{metric}</p>
      <p className="text-xs text-gray-700 leading-relaxed">{description}</p>
    </div>
  );
}

export default FraudOverview;